import React, { useMemo, useState } from 'react';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import Box from '../../components/Common/Box';
import Loader from '../../components/Common/Loader';
import Disclaimer from '../../components/Common/Disclaimer';
import PremiumLock from '../../components/Common/PremiumLock';
import { useAuth } from '../../context/AuthContext';
import { useGamificationContext } from '../../context/GamificationContext';
import { useGlobalAnalysis, AnalysisRow } from './useGlobalAnalysis';
import { GemSection } from './components/GemsSection';
import { MarketMoodGauge } from './components/MarketMoodGauge';
import { FearGreedGauge } from './components/FearGreedGauge';
import { FearGreedPanel } from './components/FearGreedPanel';
import { useFearGreed } from './useFearGreed';
import { SectorHeatmap } from './components/SectorHeatmap';
import { VolatilityTable } from './components/VolatilityTable';
import { SectorPerformanceTable } from './components/SectorPerformanceTable';
import {
  LiquidityBadge,
  LiqLongTermBadge,
  LiqCourtTermBadge,
  DivRegularityBadge,
  TechScoreBadge,
  BBSignalBadge,
} from './components/Badges';
import MobileAnalysis from './MobileAnalysis';
import { useScreenType } from '../../hooks/useScreenType';

export default function GlobalAnalysisScreen(): React.JSX.Element {
  const { hasPermission } = useAuth();
  const { trackAction } = useGamificationContext();
  const { isMobile } = useScreenType();
  const {
    loading,
    error,
    setError,
    runAnalysis,
    analysis,
    exportCSVRows,
    topPerformers,
    topYields,
    consistentDividends,
  } = useGlobalAnalysis();
  // Indice Peur & Avidité : rodage admin. Les autres comptes gardent la jauge « Sentiment de Marché ».
  const showFearGreed = hasPermission('admin_platform');
  const fearGreed = useFearGreed(showFearGreed);

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [sortColumn, setSortColumn] = useState<string>('ytd');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Pro Filters
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [filterSector, setFilterSector] = useState<string>('all');
  const [filterRSI, setFilterRSI] = useState<string>('all');
  const [filterLiqLT, setFilterLiqLT] = useState<string>('all');
  const [filterLiqCT, setFilterLiqCT] = useState<string>('all');
  const [filterDivYield, setFilterDivYield] = useState<string>('all');
  const [filterYTD, setFilterYTD] = useState<string>('all');

  // Column Visibility state
  const [visibleColumns, setVisibleColumns] = useState<string[]>([
    'symbole', 'cours_cloture', 'variation_5j_pct', 'ytd', 'rsi_last', 'tech_score', 'liquidity_score'
  ]);

  const allColumns = useMemo(() => [
    { id: 'symbole', label: 'Symbole' },
    { id: 'cours_cloture', label: 'Cours' },
    { id: 'variation_5j_pct', label: 'Var 5j' },
    { id: 'ytd', label: 'Perf (YTD)' },
    { id: 'vol30', label: 'Vol30' },
    { id: 'rsi_last', label: 'RSI' },
    { id: 'tech_score', label: 'Score Tech' },
    { id: 'bb_signal', label: 'BB Signal' },
    { id: 'cci', label: 'CCI' },
    { id: 'stoch_k', label: 'Stoch K' },
    { id: 'div_yield', label: 'Div Yield' },
    { id: 'div_regularity_score', label: 'Div Rég.' },
    { id: 'liquidity_score', label: 'Liquidité' },
    { id: 'liq_long_terme_score', label: 'Liq. LT' },
    { id: 'liq_court_terme_score', label: 'Liq. CT' },
  ], []);

  const toggleColumn = (id: string) => {
    setVisibleColumns(prev => 
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  };

  const isVisible = (id: string) => visibleColumns.includes(id);

  // Theme optimization using CSS variables natively
  const theme = useMemo(
    () => ({
      text: 'var(--text-heading)',
      sub: 'var(--text-muted)',
      bg: 'var(--bg-body)',
      boxBg: 'var(--bg-box)',
      border: '1px solid var(--border-color)',
      gridBg: 'transparent',
    }),
    []
  );

  /* ---------------------------
     Centralized filtering
     --------------------------- */
  const filteredRows = useMemo(() => {
    if (!analysis) return [];
    return analysis.rows
      .filter(r => {
        const matchSearch = r.symbole.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (r.nom || '').toLowerCase().includes(searchTerm.toLowerCase());
        const matchSector = filterSector === 'all' || r.secteur === filterSector;
        const matchRSI = filterRSI === 'all' ||
          (filterRSI === 'oversold' && r.rsi_last && r.rsi_last < 30) ||
          (filterRSI === 'neutral' && r.rsi_last && r.rsi_last >= 30 && r.rsi_last <= 70) ||
          (filterRSI === 'overbought' && r.rsi_last && r.rsi_last > 70);
        const matchLiqLT = filterLiqLT === 'all' || r.liq_long_terme_grade === filterLiqLT;
        const matchLiqCT = filterLiqCT === 'all' || r.liq_court_terme_grade === filterLiqCT;
        const matchDivYield = filterDivYield === 'all' ||
          (filterDivYield === 'high' && r.div_yield && r.div_yield >= 5) ||
          (filterDivYield === 'medium' && r.div_yield && r.div_yield >= 2 && r.div_yield < 5) ||
          (filterDivYield === 'low' && r.div_yield && r.div_yield > 0 && r.div_yield < 2) ||
          (filterDivYield === 'none' && (!r.div_yield || r.div_yield === 0));
        const matchYTD = filterYTD === 'all' ||
          (filterYTD === 'positive' && r.ytd && r.ytd > 0) ||
          (filterYTD === 'negative' && r.ytd && r.ytd < 0);
        return matchSearch && matchSector && matchRSI && matchLiqLT && matchLiqCT && matchDivYield && matchYTD;
      })
      .sort((a, b) => {
        const aVal = (a as Record<string, unknown>)[sortColumn] ?? 0;
        const bVal = (b as Record<string, unknown>)[sortColumn] ?? 0;
        if (sortColumn === 'symbole') {
          return sortDirection === 'asc'
            ? String(aVal).localeCompare(String(bVal))
            : String(bVal).localeCompare(String(aVal));
        }
        return sortDirection === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
      });
  }, [analysis, searchTerm, filterSector, filterRSI, filterLiqLT, filterLiqCT, filterDivYield, filterYTD, sortColumn, sortDirection]);

  /* ---------------------------
       UI
       --------------------------- */

  if (!hasPermission('view_advanced_data')) {
    return (
      <SiteLayout>
        <Header icon='analytics' title='Analyse Globale' />
        <div style={{ padding: 40, maxWidth: 900, margin: '0 auto' }}>
          <PremiumLock
            title="Analyse Globale du Marché"
            description="Accédez à une vue d'ensemble professionnelle du marché BRVM : carte thermique, volatilité, cycle saisonnier et bien plus. Disponible avec le plan Kanea Starter ou supérieur."
          />
        </div>
      </SiteLayout>
    );
  }

  if (error) {
    return (
      <SiteLayout>
        <Header icon='analytics' title='Analyse Globale' />
        <div style={{ padding: 40, maxWidth: 800, margin: '0 auto' }}>
          <Box>
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <h2 style={{ color: '#ef5350', marginBottom: '20px' }}>Erreur lors de l'analyse</h2>
              <div style={{ color: '#666', marginBottom: '30px', fontSize: '18px' }}>{error}</div>
              <button
                onClick={() => {
                  setError(null);
                  runAnalysis();
                }}
                style={{
                  padding: '10px 20px',
                  background: '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                }}
              >
                Réessayer
              </button>
            </div>
          </Box>
        </div>
      </SiteLayout>
    );
  }

  if (!analysis || loading) {
    return (
      <SiteLayout>
        <Header icon='analytics' title='Analyse Globale' />
        <Loader message="Chargement des données..." />
      </SiteLayout>
    );
  }

  const {
    meanYtd,
    medianYtd,
    pctUp,
    avgVol30,
    sectorData,
    rows,
    seasonality,
    marketMood,
    gems
  } = analysis;

  const ContentBox = ({ children, title, fullHeight }: { children: React.ReactNode; title?: string | React.ReactNode, fullHeight?: boolean }) => (
    <Box fullHeight={fullHeight}>
      {title && (
        <div
          className='box-title box-vertical-padding box-horizontal-padding'
          style={{ borderBottom: theme.border }}
        >
          <p style={{ color: theme.text, fontSize: 18, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>{title}</p>
        </div>
      )}
      <div style={{ padding: '20px', backgroundColor: theme.gridBg, color: theme.text, height: title ? 'calc(100% - 40px)' : '100%' }}>
        {children}
      </div>
    </Box>
  );

  /* Column header helper */
  const SortableHeader = ({ col, label, align = 'right' }: { col: string; label: string; align?: 'left' | 'right' | 'center' }) => (
    <th
      className={align}
      style={{ color: theme.sub, cursor: 'pointer' }}
      aria-sort={sortColumn === col ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
      onClick={() => {
        if (sortColumn === col) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        else { setSortColumn(col); setSortDirection('desc'); }
      }}
    >
      {label} {sortColumn === col && (sortDirection === 'asc' ? '↑' : '↓')}
    </th>
  );

  return (
    <SiteLayout>
      <Header icon='analytics' title={`Analyse Globale`} />

      <div
        style={{
          padding: isMobile ? 0 : 20,
          maxWidth: 1600,
          margin: '0 auto',
          fontFamily: "'Poppins', sans-serif",
          color: theme.text,
        }}
      >

        {isMobile ? (
          <MobileAnalysis
            analysis={analysis}
            fearGreed={showFearGreed ? fearGreed : null}
            rows={rows}
            theme={theme}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            sortColumn={sortColumn}
            setSortColumn={setSortColumn}
            sortDirection={sortDirection}
            setSortDirection={setSortDirection}
            exportCSVRows={exportCSVRows}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            filterSector={filterSector}
            setFilterSector={setFilterSector}
            filterRSI={filterRSI}
            setFilterRSI={setFilterRSI}
            filterYTD={filterYTD}
            setFilterYTD={setFilterYTD}
            filterDivYield={filterDivYield}
            setFilterDivYield={setFilterDivYield}
            trackAction={trackAction}
          />
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={exportCSVRows}
                  style={{
                    padding: '8px 16px',
                    background: '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                  }}
                >
                  Export Pro (CSV)
                </button>
              </div>
            </div>

            {/* Disclaimer */}
            <Disclaimer type="investment" />

            {/* --- PREMIUM DASHBOARD --- */}
            <div className="responsive-grid" style={{ gap: 24 }}>

              {/* 1. Market Gauge (Left) */}
              <div className="col-span-4">
                <Box fullHeight>
                  <div style={{ padding: 20, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {showFearGreed
                      ? <FearGreedGauge data={fearGreed.data} theme={theme} />
                      : <MarketMoodGauge score={marketMood} theme={theme} />}
                  </div>
                </Box>
              </div>

              {/* 2. Metrics & Gems (Right) */}
              <div className="col-span-8">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                  {/* Top: 2 Metrics side by side */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                    <Box>
                      <div style={{ padding: 20, textAlign: 'center', background: theme.boxBg }}>
                        <div style={{ fontSize: 13, color: theme.sub, marginBottom: 8 }}>Performance Moyenne</div>
                        <div style={{ fontSize: 24, fontWeight: 700, color: meanYtd >= 0 ? '#00b638' : '#ef5350' }}>{meanYtd.toFixed(2)}%</div>
                      </div>
                    </Box>
                    <Box>
                      <div style={{ padding: 20, textAlign: 'center', background: theme.boxBg }}>
                        <div style={{ fontSize: 13, color: theme.sub, marginBottom: 8 }}>Volatilité Globale</div>
                        <div style={{ fontSize: 24, fontWeight: 700, color: '#FF6D00' }}>{(avgVol30 * 100).toFixed(1)}%</div>
                      </div>
                    </Box>
                  </div>

                  {/* Bottom: Gems */}
                  <div style={{ width: '100%' }}>
                    <GemSection gems={gems} theme={theme} />
                  </div>
                </div>
              </div>

              {showFearGreed && fearGreed.data && (
                <div className="col-span-12">
                  <Box>
                    <FearGreedPanel data={fearGreed.data} meta={fearGreed.meta} theme={theme} />
                  </Box>
                </div>
              )}

              <div className="col-span-8" style={{ height: 350 }}>
                <ContentBox 
                  title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      Carte Thermique Sectorielle
                      <span
                        title="Méthodologie Kanea : Performance moyenne équipondérée (Equal-Weight). Reflète la largeur du marché (Market Breadth) et évite le biais de concentration des méga-caps. Sources: 'The Cross-Section of Expected Stock Returns' (Fama & French, 1992) & 'Fundamental Indexation' (Arnott et al., 2005)."
                        style={{ cursor: 'help', fontSize: 16, color: '#3b82f6', display: 'flex', alignItems: 'center' }}
                      >
                        ⓘ
                      </span>
                    </div>
                  } 
                  fullHeight
                >
                  <SectorHeatmap data={sectorData} theme={theme} />
                </ContentBox>
              </div>

              <div className="col-span-4" style={{ height: 350 }}>
                <ContentBox title="Cycle Saisonnier (Historique)" fullHeight>
                  <div
                    style={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      overflow: 'auto'
                    }}
                  >
                    {seasonality.length === 0 ? (
                      <div style={{ textAlign: 'center', color: theme.sub, fontSize: 13, padding: 20 }}>
                        Aucune donnée saisonnière disponible. Le cache doit être recalculé.
                      </div>
                    ) : (
                      seasonality.map((m) => (
                        <div
                          key={m.month}
                          style={{ display: 'flex', alignItems: 'center', marginBottom: 6, fontSize: 11 }}
                        >
                          <div style={{ width: 40, color: theme.sub }}>
                            {new Date(0, m.month).toLocaleString('fr-FR', { month: 'short' })}
                          </div>
                          <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                            <div
                              style={{
                                height: 8,
                                width: `${Math.abs(m.avgChange) * 10}%`,
                                backgroundColor: m.avgChange >= 0 ? '#00b638' : '#ef5350',
                                borderRadius: 2,
                                minWidth: 2,
                              }}
                            />
                            <div style={{ marginLeft: 8, color: theme.text, fontWeight: 'bold' }}>
                              {m.avgChange > 0 ? '+' : ''}
                              {m.avgChange.toFixed(2)}%
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </ContentBox>
              </div>

              {/* 5. Volatility & Sector Performance Tables */}
              <div className="col-span-6" style={{ height: 400 }}>
                <ContentBox title="Top 10 Volatilité" fullHeight>
                  <VolatilityTable rows={rows} theme={theme} />
                </ContentBox>
              </div>

              <div className="col-span-6" style={{ height: 400 }}>
                <ContentBox title="Performance par Secteur (YTD)" fullHeight>
                  <SectorPerformanceTable sectorData={sectorData} theme={theme} />
                </ContentBox>
              </div>

              {/* 6. Advanced Screener with Search & Sort */}
              <div className="col-span-12">
                <ContentBox title="Scanner Technique">
                  {/* Search Input and Filters Toggle */}
                  <div style={{ marginBottom: 15, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    <input
                      type="text"
                      placeholder="🔍 Rechercher par symbole ou nom..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{
                        padding: '10px 15px',
                        borderRadius: 8,
                        border: theme.border,
                        background: theme.boxBg,
                        color: theme.text,
                        fontSize: 14,
                        width: 300,
                      }}
                    />
                    <button
                      onClick={() => {
                        setShowFilters(!showFilters);
                        if (!showFilters) trackAction('USE_SCREENER_PRO');
                      }}
                      style={{
                        padding: '10px 15px',
                        borderRadius: 8,
                        border: showFilters ? '2px solid #3b82f6' : theme.border,
                        background: showFilters ? 'rgba(59, 130, 246, 0.1)' : theme.boxBg,
                        color: showFilters ? '#3b82f6' : theme.text,
                        fontSize: 13,
                        cursor: 'pointer',
                        fontWeight: showFilters ? 'bold' : 'normal',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6
                      }}
                    >
                      ⚙️ Filtres Pro {showFilters ? '▲' : '▼'}
                    </button>

                    {/* Column Picker UI */}
                    <div style={{ position: 'relative' }}>
                      <select 
                        onChange={(e) => toggleColumn(e.target.value)}
                        value=""
                        style={{
                          padding: '10px 15px',
                          borderRadius: 8,
                          border: theme.border,
                          background: theme.boxBg,
                          color: theme.text,
                          fontSize: 13,
                          cursor: 'pointer',
                        }}
                      >
                        <option value="" disabled>📊 Colonnes...</option>
                        {allColumns.map(col => (
                          <option key={col.id} value={col.id}>
                            {isVisible(col.id) ? '✓' : '○'} {col.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {(filterSector !== 'all' || filterRSI !== 'all' || filterLiqLT !== 'all' || filterLiqCT !== 'all' || filterDivYield !== 'all' || filterYTD !== 'all') && (
                      <button
                        onClick={() => {
                          setFilterSector('all');
                          setFilterRSI('all');
                          setFilterLiqLT('all');
                          setFilterLiqCT('all');
                          setFilterDivYield('all');
                          setFilterYTD('all');
                        }}
                        style={{
                          padding: '8px 12px',
                          borderRadius: 6,
                          border: 'none',
                          background: 'rgba(220, 38, 38, 0.15)',
                          color: '#ef4444',
                          fontSize: 11,
                          cursor: 'pointer',
                        }}
                      >
                        ✕ Reset filtres
                      </button>
                    )}
                    <span style={{ fontSize: 11, color: theme.sub, marginLeft: 'auto' }}>
                      {filteredRows.length} résultats
                    </span>
                  </div>

                  {/* Pro Filters Panel */}
                  {showFilters && (
                    <div style={{
                      marginBottom: 20,
                      padding: 15,
                      borderRadius: 10,
                      background: 'var(--bg-input)',
                      border: theme.border,
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                      gap: 15
                    }}>
                      {/* Secteur */}
                      <div>
                        <label style={{ fontSize: 11, color: theme.sub, display: 'block', marginBottom: 4 }}>Secteur</label>
                        <select
                          value={filterSector}
                          onChange={(e) => setFilterSector(e.target.value)}
                          style={{
                            width: '100%', padding: 8, borderRadius: 6,
                            border: theme.border, background: theme.boxBg, color: theme.text, fontSize: 12
                          }}
                        >
                          <option value="all">Tous</option>
                          {[...new Set(rows.map(r => r.secteur).filter(Boolean))].sort().map(s => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </div>

                      {/* RSI */}
                      <div>
                        <label style={{ fontSize: 11, color: theme.sub, display: 'block', marginBottom: 4 }}>RSI</label>
                        <select
                          value={filterRSI}
                          onChange={(e) => setFilterRSI(e.target.value)}
                          style={{
                            width: '100%', padding: 8, borderRadius: 6,
                            border: theme.border, background: theme.boxBg, color: theme.text, fontSize: 13
                          }}
                        >
                          <option value="all">Tous</option>
                          <option value="oversold">🟢 Survendu (&lt;30)</option>
                          <option value="neutral">⚪ Neutre (30-70)</option>
                          <option value="overbought">🔴 Suracheté (&gt;70)</option>
                        </select>
                      </div>

                      {/* Liquidité LT */}
                      <div>
                        <label style={{ fontSize: 11, color: theme.sub, display: 'block', marginBottom: 4 }}>Liquidité LT</label>
                        <select
                          value={filterLiqLT}
                          onChange={(e) => setFilterLiqLT(e.target.value)}
                          style={{
                            width: '100%', padding: 8, borderRadius: 6,
                            border: theme.border, background: theme.boxBg, color: theme.text, fontSize: 13
                          }}
                        >
                          <option value="all">Tous</option>
                          <option value="EXCELLENTE">🟢 Excellente</option>
                          <option value="FRAGILE">🟡 Fragile</option>
                          <option value="FAIBLE">🟠 Faible</option>
                          <option value="ILLIQUIDE">🔴 Illiquide</option>
                        </select>
                      </div>

                      {/* Liquidité CT */}
                      <div>
                        <label style={{ fontSize: 11, color: theme.sub, display: 'block', marginBottom: 4 }}>Liquidité CT</label>
                        <select
                          value={filterLiqCT}
                          onChange={(e) => setFilterLiqCT(e.target.value)}
                          style={{
                            width: '100%', padding: 8, borderRadius: 6,
                            border: theme.border, background: theme.boxBg, color: theme.text, fontSize: 12
                          }}
                        >
                          <option value="all">Tous</option>
                          <option value="EXCELLENTE">🟢 Excellente</option>
                          <option value="FRAGILE">🟡 Fragile</option>
                          <option value="FAIBLE">🟠 Faible</option>
                          <option value="BLOQUE">⛔ Bloqué</option>
                        </select>
                      </div>

                      {/* Rendement */}
                      <div>
                        <label style={{ fontSize: 11, color: theme.sub, display: 'block', marginBottom: 4 }}>Rendement Div</label>
                        <select
                          value={filterDivYield}
                          onChange={(e) => setFilterDivYield(e.target.value)}
                          style={{
                            width: '100%', padding: 8, borderRadius: 6,
                            border: theme.border, background: theme.boxBg, color: theme.text, fontSize: 12
                          }}
                        >
                          <option value="all">Tous</option>
                          <option value="high">🔥 Élevé (&gt;5%)</option>
                          <option value="medium">💰 Moyen (2-5%)</option>
                          <option value="low">📉 Faible (&lt;2%)</option>
                          <option value="none">❌ Aucun</option>
                        </select>
                      </div>

                      {/* Performance YTD */}
                      <div>
                        <label style={{ fontSize: 11, color: theme.sub, display: 'block', marginBottom: 4 }}>Perf YTD</label>
                        <select
                          value={filterYTD}
                          onChange={(e) => setFilterYTD(e.target.value)}
                          style={{
                            width: '100%', padding: 8, borderRadius: 6,
                            border: theme.border, background: theme.boxBg, color: theme.text, fontSize: 12
                          }}
                        >
                          <option value="all">Tous</option>
                          <option value="positive">📈 Positive</option>
                          <option value="negative">📉 Négative</option>
                        </select>
                      </div>
                    </div>
                  )}

                  <div style={{ maxHeight: 600, overflow: 'auto' }}>
                    <table className='data-table' style={{ width: '100%' }}>
                      <thead>
                        <tr>
                          {isVisible('symbole') && <SortableHeader col="symbole" label="Symbole" align="left" />}
                          {isVisible('variation_5j_pct') && <SortableHeader col="variation_5j_pct" label="Var 5j" align="right" />}
                          {isVisible('ytd') && <SortableHeader col="ytd" label="Perf (YTD)" align="right" />}
                          {isVisible('cours_cloture') && <SortableHeader col="cours_cloture" label="Cours" align="right" />}
                          {isVisible('vol30') && <SortableHeader col="vol30" label="Vol30" align="right" />}
                          {isVisible('rsi_last') && <SortableHeader col="rsi_last" label="RSI" align="right" />}
                          {isVisible('tech_score') && <SortableHeader col="tech_score" label="Score Tech" align="center" />}
                          {isVisible('bb_signal') && <th className='center' style={{ color: theme.sub }}>BB Signal</th>}
                          {isVisible('cci') && <SortableHeader col="cci" label="CCI" align="right" />}
                          {isVisible('stoch_k') && <SortableHeader col="stoch_k" label="Stoch K" align="right" />}
                          {isVisible('div_yield') && <SortableHeader col="div_yield" label="Div Yield" align="right" />}
                          {isVisible('div_regularity_score') && <SortableHeader col="div_regularity_score" label="Div Rég." align="center" />}
                          {isVisible('liquidity_score') && <SortableHeader col="liquidity_score" label="Liquidité" align="center" />}
                          {isVisible('liq_long_terme_score') && <SortableHeader col="liq_long_terme_score" label="Liq. LT" align="center" />}
                          {isVisible('liq_court_terme_score') && <SortableHeader col="liq_court_terme_score" label="Liq. CT" align="center" />}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredRows.map((p) => (
                          <tr key={p.symbole}>
                            {isVisible('symbole') && (
                              <td className='left' style={{ color: theme.text, fontWeight: 'bold' }}>
                                {p.symbole}
                                <div style={{ fontSize: 12, color: theme.sub, fontWeight: 'normal' }}>{p.nom}</div>
                              </td>
                            )}
                            {isVisible('variation_5j_pct') && (
                              <td
                                className='right'
                                style={{
                                  fontWeight: 'bold',
                                  color: (p.variation_5j_pct ?? 0) >= 0 ? '#00b638' : '#ef5350',
                                }}
                              >
                                {p.variation_5j_pct != null ? `${p.variation_5j_pct >= 0 ? '+' : ''}${p.variation_5j_pct.toFixed(2)}%` : '—'}
                              </td>
                            )}
                            {isVisible('ytd') && (
                              <td
                                className='right'
                                style={{
                                  fontWeight: 'bold',
                                  color: (p.ytd ?? 0) >= 0 ? '#00b638' : '#ef5350',
                                }}
                              >
                                {(p.ytd ?? 0).toFixed(2)}%
                              </td>
                            )}
                            {isVisible('cours_cloture') && (
                              <td className='right' style={{ color: theme.text }}>
                                {p.cours_cloture?.toLocaleString()} FCFA
                              </td>
                            )}
                            {isVisible('vol30') && (
                              <td className='right' style={{ color: theme.text }}>
                                {p.vol30 ? (p.vol30 * 100).toFixed(1) + '%' : 'N/A'}
                              </td>
                            )}
                            {isVisible('rsi_last') && (
                              <td
                                className='right'
                                style={{
                                  color:
                                    p.rsi_last && (p.rsi_last > 70 || p.rsi_last < 30)
                                      ? p.rsi_last > 70
                                        ? '#ef5350'
                                        : '#00b638'
                                      : theme.text,
                                }}
                              >
                                {p.rsi_last ? p.rsi_last.toFixed(1) : 'N/A'}
                              </td>
                            )}
                            {isVisible('tech_score') && (
                              <td className='center'>
                                <TechScoreBadge score={p.tech_score} />
                              </td>
                            )}
                            {isVisible('bb_signal') && (
                              <td className='center'>
                                <BBSignalBadge signal={p.bb_signal} />
                              </td>
                            )}
                            {isVisible('cci') && (
                              <td className='right' style={{ color: theme.text }}>
                                {p.cci != null ? p.cci.toFixed(0) : '—'}
                              </td>
                            )}
                            {isVisible('stoch_k') && (
                              <td className='right' style={{ color: theme.text }}>
                                {p.stoch_k != null ? `${p.stoch_k.toFixed(0)}/${p.stoch_d?.toFixed(0) ?? '—'}` : '—'}
                              </td>
                            )}
                            {isVisible('div_yield') && (
                              <td className='right' style={{ color: '#2962FF' }}>
                                {p.div_yield != null ? `${(p.div_yield ?? 0).toFixed(2)}%` : '—'}
                              </td>
                            )}
                            {isVisible('div_regularity_score') && (
                              <td className='center'>
                                <DivRegularityBadge score={p.div_regularity_score ?? 0} />
                              </td>
                            )}
                            {isVisible('liquidity_score') && (
                              <td className='center'>
                                <LiquidityBadge grade={p.liquidity_grade} />
                              </td>
                            )}
                            {isVisible('liq_long_terme_score') && (
                              <td className='center'>
                                <LiqLongTermBadge grade={p.liq_long_terme_grade} score={p.liq_long_terme_score} />
                              </td>
                            )}
                            {isVisible('liq_court_terme_score') && (
                              <td className='center'>
                                <LiqCourtTermBadge grade={p.liq_court_terme_grade} score={p.liq_court_terme_score} isBlocked={p.liq_ct_is_blocked} />
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </ContentBox>
              </div>

            </div>

            <footer style={{ marginTop: 40, textAlign: 'center', color: theme.sub, fontSize: '12px' }}>
              Analyse Globale du Marché · Généré le {new Date().toLocaleString()}
            </footer>
          </>
        )}
      </div>
    </SiteLayout>
  );
}
